import { useEffect, useState, type AnimationEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import {
  getLanguage,
  isAppLanguage,
  normalizeLanguage,
  SUPPORTED_LANGUAGES,
  type AppLanguage,
} from '../../i18n/languages';
import { setAppLanguage } from '../../i18n';

interface LanguageSheetProps {
  open: boolean;
  onClose: () => void;
}

const CLOSE_FALLBACK_MS = 300;

/**
 * Controlled bottom sheet — the trigger lives elsewhere (the drawer's
 * "Language" row). Same open/close animation architecture as the wallet
 * sheet and the hamburger drawer: `closing` plays the CSS out-animation,
 * then `onAnimationEnd` (with a timeout fallback) calls `onClose`.
 */
export function LanguageSheet({ open, onClose }: LanguageSheetProps) {
  const { t, i18n } = useTranslation();
  const [closing, setClosing] = useState(false);

  const currentCode =
    (isAppLanguage(i18n.resolvedLanguage) && i18n.resolvedLanguage) || normalizeLanguage(i18n.language) || 'en';

  const requestClose = () => {
    if (closing) return;
    setClosing(true);
  };

  useEffect(() => {
    if (!closing) return;
    const id = window.setTimeout(() => {
      setClosing(false);
      onClose();
    }, CLOSE_FALLBACK_MS);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closing]);

  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, closing]);

  const handleSheetAnimationEnd = (event: AnimationEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (!closing) return;
    if (!event.animationName.includes('cs-language-sheet-out')) return;
    setClosing(false);
    onClose();
  };

  const selectLanguage = async (code: AppLanguage) => {
    await setAppLanguage(code);
    requestClose();
  };

  if (!open && !closing) return null;

  return createPortal(
    <>
      <div
        className={`cs-language-sheet__backdrop${closing ? ' cs-language-sheet__backdrop--closing' : ''}`}
        onClick={requestClose}
        aria-hidden
      />
      <div
        className={`cs-language-sheet${closing ? ' cs-language-sheet--closing' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={t('settings.chooseLanguage')}
        onAnimationEnd={handleSheetAnimationEnd}
      >
        <div className="cs-language-sheet__handle" aria-hidden />

        <div className="cs-language-sheet__header">
          <h2 className="cs-language-sheet__title">{t('settings.chooseLanguage')}</h2>
          <button
            type="button"
            className="cs-language-sheet__close"
            aria-label={t('common.cancel')}
            onClick={requestClose}
          >
            <X size={18} strokeWidth={2.25} />
          </button>
        </div>

        <div className="cs-language-sheet__list" role="listbox" aria-label={t('settings.chooseLanguage')}>
          {SUPPORTED_LANGUAGES.map((language) => {
            const active = language.code === currentCode;
            return (
              <button
                key={language.code}
                type="button"
                role="option"
                aria-selected={active}
                className={`cs-language-sheet__option${active ? ' cs-language-sheet__option--active' : ''}`}
                onClick={() => void selectLanguage(language.code)}
              >
                <span className="cs-language-sheet__flag" aria-hidden>
                  {language.flag}
                </span>
                <span className="cs-language-sheet__copy">
                  <strong>{language.nativeLabel}</strong>
                  <small>{language.label}</small>
                </span>
                {active && (
                  <span className="cs-language-sheet__check" aria-hidden>
                    <Check size={16} strokeWidth={2.75} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </>,
    document.body,
  );
}

/** Convenience for callers that only need the resolved flag/label of the current language. */
export function useCurrentLanguage() {
  const { i18n } = useTranslation();
  const code =
    (isAppLanguage(i18n.resolvedLanguage) && i18n.resolvedLanguage) || normalizeLanguage(i18n.language) || 'en';
  return getLanguage(code);
}
